// ─────────────────────────────────────────────────────────────────────────────
// Bekannte Bugs & geplante Verbesserungen.
//
// Beim Erledigen eines Eintrags: Status auf 'erledigt' setzen UND einen
// passenden Eintrag in releases.ts ergänzen. Erledigte Einträge können
// nach einem Release auch entfernt werden – sie tauchen ja in den
// Release-Notes selbst auf.
// ─────────────────────────────────────────────────────────────────────────────

export type BugCategory =
  | 'Bug'
  | 'Verbesserung'
  | 'Feature'
  | 'Sicherheit';

export type BugPriority =
  | 'kritisch'
  | 'hoch'
  | 'mittel'
  | 'niedrig';

export type BugStatus =
  | 'offen'
  | 'in Arbeit'
  | 'erledigt'
  | 'verworfen';

export interface Bug {
  id: string;                   // z.B. 'BUG-001'
  title: string;                // Kurzbeschreibung
  description?: string;         // optional, ausführlicher
  area?: string;                // betroffener Bereich, z.B. 'Karte', 'Leistungsverzeichnis'
  category: BugCategory;
  priority: BugPriority;
  status: BugStatus;
  targetVersion?: string;       // geplante Ziel-Version, z.B. '1.3.0'
  reportedDate?: string;        // YYYY-MM-DD
  resolvedDate?: string;        // YYYY-MM-DD (gesetzt beim Erledigen)
  resolvedInVersion?: string;   // in welchem Release behoben
}

export const bugs: Bug[] = [
  {
    id: 'FEAT-047',
    title: 'Neue Rolle „Bauhof"',
    description: 'Rolle bauhof (ENUM user_role erweitert, Migration 2026-09-16_role_bauhof.sql). Rechte: ansehen; eigene zugewiesene Maßnahmen im Status ändern (measures PUT mit Eigen-Prüfung); Schäden anlegen + behoben (damages POST/PUT). Kein canEdit, kein Zuweisen, keine Verwaltung. Sidebar/Historie (nur eigene) entsprechend. Rollen-Dropdown in Users.tsx.',
    area: 'Rollen / Rechte',
    category: 'Feature',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.13.0',
    resolvedInVersion: '1.13.0',
    reportedDate: '2026-09-23',
    resolvedDate: '2026-09-23',
  },
  {
    id: 'IMP-032',
    title: 'Rechte vereinheitlicht (Erfasser/Betrachter/Löschen)',
    description: 'Erfasser: volle Historie (nur betrachter+bauhof auf eigene beschränkt); Zuweisung am Baum via /users/selectable statt admin-only /users. Löschen (Bäume/Maßnahmen/Schäden/Kontrollen/LV, Bereich-Löschen) durchgängig admin-only – Backend-requireRole + Frontend-Buttons angeglichen. bulk-fell bleibt admin+erfasser.',
    area: 'Rollen / Rechte',
    category: 'Verbesserung',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.13.0',
    resolvedInVersion: '1.13.0',
    reportedDate: '2026-09-23',
    resolvedDate: '2026-09-23',
  },
  {
    id: 'BUG-033',
    title: 'Kontrolle: kein Schaden bei „ohne besonderen Befund"',
    description: 'Auto-Schaden aus Kontrolle filtert o.b.B./„ohne Befund"/„keine Mängel"/„unauffällig" heraus (Merkmale + Befund-Zeilen). Herkunftsvermerk „Aus Baumkontrolle vom …" in der Beschreibung.',
    area: 'Kontrolle',
    category: 'Bug',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.13.0',
    resolvedInVersion: '1.13.0',
    reportedDate: '2026-09-04',
    resolvedDate: '2026-09-04',
  },
  {
    id: 'BUG-034',
    title: 'Luftbild-Fallback (keine graue Karte)',
    description: 'Esri-Weltluftbild wieder als Unterlage unter dem amtlichen DOP20 (deckend). Fällt der Behörden-WMS kurz aus, scheint Esri durch statt grau.',
    area: 'Karte',
    category: 'Bug',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.13.0',
    resolvedInVersion: '1.13.0',
    reportedDate: '2026-09-16',
    resolvedDate: '2026-09-16',
  },
  {
    id: 'BUG-035',
    title: 'Benutzer aktivieren/deaktivieren repariert',
    description: 'Fehlender Endpoint PUT /api/users/:id/toggle-active ergänzt (admin-only, Selbst-Deaktivierungs-Schutz, Audit). Button in der Benutzerverwaltung funktionierte zuvor nicht (404).',
    area: 'Benutzerverwaltung',
    category: 'Bug',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.13.0',
    resolvedInVersion: '1.13.0',
    reportedDate: '2026-09-16',
    resolvedDate: '2026-09-16',
  },
  {
    id: 'FEAT-043',
    title: 'LV: Baummaße + Maßnahmen-Beschreibung anzeigen',
    description: 'Baummaße (hoehe/stdm/kdm) und description werden in der Auswahl-Tabelle, der Detail-Box und im PDF gezeigt. Backend: MEASURES_SELECT + LV-Positionen-Query um t.hoehe/t.stdm/t.kdm erweitert (live aus trees).',
    area: 'Leistungsverzeichnis',
    category: 'Feature',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.12.5',
    resolvedInVersion: '1.12.5',
    reportedDate: '2026-09-04',
    resolvedDate: '2026-09-04',
  },
  {
    id: 'FEAT-044',
    title: 'LV-PDF: Gemeinde-Briefkopf + Logo',
    description: 'PDF-Layout offizieller (Briefkopf mit Absender + Logo statt Emoji). Neuer Endpoint GET /api/settings/pdf-kopf liefert Logo (Data-URL) + Gemeinde-Kopfdaten je Instanz. Logo mandantenspezifisch: gemeinde_logo_<kunde>.(png|jpg|svg) mit Fallback gemeinde_logo.*; optional gemeinde_<kunde>.json / gemeinde.json.',
    area: 'Leistungsverzeichnis',
    category: 'Feature',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.12.5',
    resolvedInVersion: '1.12.5',
    reportedDate: '2026-09-04',
    resolvedDate: '2026-09-04',
  },
  {
    id: 'FEAT-045',
    title: 'Baumkontrolle: Durchführungszeitraum',
    description: 'Neues Select im Kontroll-Formular (unverzüglich/2/4/6/12 Wochen/bis zur nächsten Regelkontrolle). Migration 2026-09-04_control_durchfuehrungszeitraum.sql (Spalte durchfuehrungszeitraum an tree_controls). Anzeige in Kontroll-Box + Kontroll-PDF.',
    area: 'Kontrolle',
    category: 'Feature',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.12.5',
    resolvedInVersion: '1.12.5',
    reportedDate: '2026-09-04',
    resolvedDate: '2026-09-04',
  },
  {
    id: 'FEAT-046',
    title: 'Kontrolle → automatisch Schäden am Baum',
    description: 'Beim Speichern einer Kontrolle wird je Sektion (Krone/Stamm/Wurzeln) mit Schadmerkmal/Befund ein tree_damage angelegt (location_on_tree, damage_type aus Merkmalen, description aus Befund, Priorität aus sektionsbezogener Verkehrssicherheit). Maßnahmen-Vorschlag bei „Nicht gegeben" bleibt erhalten.',
    area: 'Kontrolle',
    category: 'Feature',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.12.5',
    resolvedInVersion: '1.12.5',
    reportedDate: '2026-09-04',
    resolvedDate: '2026-09-04',
  },
  {
    id: 'IMP-030',
    title: 'Kontroll-PDF: Baumwächter-Logo',
    description: 'Logo (/logo.png) im Kopf des Kontroll-PDF statt 🌳-Emoji, gestapelt über der Überschrift. Kurze Druck-Verzögerung, damit das Logo geladen ist.',
    area: 'Kontrolle',
    category: 'Verbesserung',
    priority: 'niedrig',
    status: 'erledigt',
    targetVersion: '1.12.5',
    resolvedInVersion: '1.12.5',
    reportedDate: '2026-09-04',
    resolvedDate: '2026-09-04',
  },
  {
    id: 'IMP-031',
    title: 'Mobile: Inhalte nicht mehr hinter der Navileiste',
    description: 'AppLayout auf festes 100dvh + overflow hidden umgestellt, minHeight:0 an Spalte/Content für sauberes internes Scrollen, unterer Sicherheitsabstand über der fixen Bottom-Navigation. Betraf zuvor Dashboard, LV, Auswertungen, Kataster.',
    area: 'Mobile / UI',
    category: 'Verbesserung',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.12.5',
    resolvedInVersion: '1.12.5',
    reportedDate: '2026-09-04',
    resolvedDate: '2026-09-04',
  },
  {
    id: 'IMP-029',
    title: 'Hochauflösendes Luftbild (DOP20) + näher zoombar',
    description: 'Luftbild-Modus nutzt jetzt das amtliche DOP20 (20 cm, Sachsen-Anhalt, unbeschrifteter Layer lsa_lvermgeo_dop20_2, kostenfrei DL-DE-BY 2.0) als WMSTileLayer statt Esri. maxZoom 19→21 (OSM/Luftbild mit maxNativeZoom hochskaliert). Leaflet-Attribution auf Mobile ausgeblendet (attributionControl={!isMobile}), da sie mit den Zoom-Buttons überlappte.',
    area: 'Karte',
    category: 'Verbesserung',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.12.4',
    resolvedInVersion: '1.12.4',
    reportedDate: '2026-08-28',
    resolvedDate: '2026-08-28',
  },
  {
    id: 'FEAT-041',
    title: 'Schäden bestätigen/prüfen (ohne Maßnahme)',
    description: 'Schäden lassen sich als „geprüft" bestätigen (bestaetigt_von/_am/_notiz), bleiben sichtbar, zählen aber nicht mehr als offener Handlungsbedarf. Gültigkeit ans Kontrollintervall des Baums gekoppelt (danach „veraltet" + wieder offen). Endpoint PUT /api/damages/:id/bestaetigen. Anzeige im TreeDetail, PDF-Export und grüner Haken in der Baumliste. Migration 2026-08-27_damage_bestaetigt.sql.',
    area: 'Schäden',
    category: 'Feature',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.12.4',
    resolvedInVersion: '1.12.4',
    reportedDate: '2026-08-28',
    resolvedDate: '2026-08-28',
  },
  {
    id: 'FEAT-042',
    title: 'ALKIS-Flurstücke als Karten-Layer',
    description: 'Zuschaltbarer Layer „Flurstücke (Eigentum)" in der Karte. Lädt GeoJSON je Instanz vom Backend (GET /api/alkis/eigentum, Datei backend/data/alkis_eigentum.geojson). Zeichnet Punkte und Polygone (eigene Pane unter den Baum-Markern, damit Bäume klickbar bleiben).',
    area: 'Karte',
    category: 'Feature',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.12.4',
    resolvedInVersion: '1.12.4',
    reportedDate: '2026-08-28',
    resolvedDate: '2026-08-28',
  },
  {
    id: 'IMP-027',
    title: 'Rate-Limit erhöht (Sammel-Aktionen)',
    description: 'Schreib-Limit 200→1000/15min, Lese-Limit 1500→3000/15min in index.ts. Behebt 429 beim Bereich-Löschen/-Fällen auf großen Instanzen (z.B. vorharz). Weiterhin per .env überschreibbar.',
    area: 'Infrastruktur',
    category: 'Verbesserung',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.12.4',
    resolvedInVersion: '1.12.4',
    reportedDate: '2026-08-28',
    resolvedDate: '2026-08-28',
  },
  {
    id: 'IMP-028',
    title: 'Mobile-Feinschliff (Bottom-Nav, Schaden-Karte)',
    description: 'Bottom-Navigation „Meine Aufgaben"→„Aufgaben" und insgesamt größer/klarer. Schaden-Karte auf dem Handy aufgeräumt: nur Primär-Aktionen sichtbar, Rest im ⋯-Überlaufmenü.',
    area: 'Mobile / UI',
    category: 'Verbesserung',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.12.4',
    resolvedInVersion: '1.12.4',
    reportedDate: '2026-08-28',
    resolvedDate: '2026-08-28',
  },
  {
    id: 'FEAT-039',
    title: 'Aufgaben an andere Nutzer zuweisen',
    description: 'Sammel-Dialog (Baumkataster) und „Neue Aufgabe" (Meine Aufgaben) haben ein „Zuweisen an"-Feld (nur admin + erfasser). Zugewiesener Nutzer erhält automatisch eine Benachrichtigung. Nutzt /users/selectable.',
    area: 'Aufgaben',
    category: 'Feature',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.12.3',
    resolvedInVersion: '1.12.3',
    reportedDate: '2026-08-27',
    resolvedDate: '2026-08-27',
  },
  {
    id: 'FEAT-040',
    title: 'Meine Aufgaben: erledigte anzeigen + Wieder öffnen',
    description: 'Umschalter Offen/Erledigt (Endpoint /measures/my-tasks?status=erledigt). Status-Chip korrekt für erledigt/abgebrochen, „Wieder öffnen"-Aktion, Erledigungsdatum in der Meta-Zeile.',
    area: 'Aufgaben',
    category: 'Feature',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.12.3',
    resolvedInVersion: '1.12.3',
    reportedDate: '2026-08-27',
    resolvedDate: '2026-08-27',
  },
  {
    id: 'IMP-025',
    title: 'Historie nur eigene für Nicht-Admins',
    description: 'buildAuditLogWhere schränkt erfasser + betrachter auf eigene user_id ein (Liste, Zähler, Export); filter-options liefern nur eigene; Benutzer-Filter im Frontend für Nicht-Admins ausgeblendet.',
    area: 'Rechte / Historie',
    category: 'Verbesserung',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.12.3',
    resolvedInVersion: '1.12.3',
    reportedDate: '2026-08-27',
    resolvedDate: '2026-08-27',
  },
  {
    id: 'BUG-032',
    title: 'Bereich-Löschen: 429 + Historie',
    description: 'Bereich-Löschen/-Fällen feuerte viele parallele Requests (429 Too Many Requests). Neu: Bulk-Endpoints /trees/bulk-delete und /trees/bulk-fell (ein Request), gebündelter Historien-Eintrag mit Anzahl und Baumnummern.',
    area: 'Karte',
    category: 'Bug',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.12.3',
    resolvedInVersion: '1.12.3',
    reportedDate: '2026-08-27',
    resolvedDate: '2026-08-27',
  },
  {
    id: 'IMP-026',
    title: 'Feinschliff: Banner, Karte, PWA, Nutzerauswahl',
    description: '„Was ist neu"-Banner im Dashboard; Karten-Koordinaten neu positioniert/mobil aus; Funktion-Feld ohne Label-Überlappung; PWA-Safe-Area (Kopfzeile/Bottom-Nav); /users/selectable liefert auch Nutzer mit active=NULL.',
    area: 'Diverses',
    category: 'Verbesserung',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.12.3',
    resolvedInVersion: '1.12.3',
    reportedDate: '2026-08-27',
    resolvedDate: '2026-08-27',
  },
  {
    id: 'BUG-031',
    title: 'Leistungsverzeichnis: PDF-Export einzelner LVs (500)',
    description: 'Der LV-PDF-Endpoint las aus einer nicht existierenden Tabelle „lv" und jointe eine nicht vorhandene Spalte tree_id → 500. Auf leistungsverzeichnisse korrigiert und Positionsdaten direkt aus lv_positionen gelesen.',
    area: 'Leistungsverzeichnis',
    category: 'Bug',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.12.2',
    resolvedInVersion: '1.12.2',
    reportedDate: '2026-08-23',
    resolvedDate: '2026-08-23',
  },
  {
    id: 'FEAT-038',
    title: 'Auswertung: Tab „Verkehrssicherheit"',
    description: 'Neuer Endpoint /reports/verkehrssicherheit + Tab: Verteilung (Ampel), kontrolliert vs. überfällig, nicht verkehrssicher pro Ort, Kontrollen pro Monat. Nachweis der Verkehrssicherungspflicht.',
    area: 'Auswertung',
    category: 'Feature',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.12.2',
    resolvedInVersion: '1.12.2',
    reportedDate: '2026-08-23',
    resolvedDate: '2026-08-23',
  },
  {
    id: 'IMP-023',
    title: 'Auswertung (Maßnahmen) ausgebaut',
    description: '„Kosten pro Ort" aus LV-Kosten, „Kosten pro Bauhof-Mitarbeiter" aus Bauhof-LVs, neue Aufteilung Intern (Bauhof) vs. extern (Firma) und animiertes Donut „Maßnahmen nach Status".',
    area: 'Auswertung',
    category: 'Verbesserung',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.12.2',
    resolvedInVersion: '1.12.2',
    reportedDate: '2026-08-23',
    resolvedDate: '2026-08-23',
  },
  {
    id: 'IMP-024',
    title: 'Karte mobil: volle Höhe (dvh)',
    description: 'Kartenhöhe auf 100dvh umgestellt – die Karte überlappt auf kleineren Handys nicht mehr mit der Menüleiste.',
    area: 'Karte',
    category: 'Verbesserung',
    priority: 'niedrig',
    status: 'erledigt',
    targetVersion: '1.12.2',
    resolvedInVersion: '1.12.2',
    reportedDate: '2026-08-23',
    resolvedDate: '2026-08-23',
  },
  {
    id: 'FEAT-037',
    title: 'Roadmap & Ideen (Tab unter Release Notes)',
    description: 'Zeigt offene Punkte und Ideen aus roadmap.ts nach Themen gruppiert (Status/Prio/Aufwand-Chips, Filter). Als Tab in die Release-Notes-Seite integriert. Pflege über frontend/src/data/roadmap.ts.',
    area: 'App / Info',
    category: 'Feature',
    priority: 'niedrig',
    status: 'erledigt',
    targetVersion: '1.12.2',
    resolvedInVersion: '1.12.2',
    reportedDate: '2026-08-23',
    resolvedDate: '2026-08-23',
  },
  {
    id: 'IMP-022',
    title: 'Karte mobil optimiert',
    description: 'Volle Kartenhöhe auf dem Handy; die Aktions-Buttons (Baum/Fläche/Bereich) liegen hinter einem ausklappbaren „+"-Menü (SpeedDial), das Anzeige-/Legenden-Panel ist ein-/ausklappbar und startet mobil eingeklappt.',
    area: 'Karte',
    category: 'Verbesserung',
    priority: 'niedrig',
    status: 'erledigt',
    targetVersion: '1.12.0',
    resolvedInVersion: '1.12.0',
    reportedDate: '2026-08-23',
    resolvedDate: '2026-08-23',
  },
  {
    id: 'FEAT-035',
    title: 'Leistungsverzeichnis: Auftrag an Firma oder Bauhof (Stundenlohn)',
    description: 'Umschalter „Auftrag an: Firma / Bauhof". Bei Bauhof Abrechnung nach Stundenlohn × Stunden, mit Mehrfach-Auswahl der zuständigen Mitarbeiter (aus aktiven Nutzern, frei ergänzbar). Neue Spalten auftrag_art, bauhof_mitarbeiter, stundenlohn, stunden; schlanker /users/selectable-Endpoint.',
    area: 'Leistungsverzeichnis',
    category: 'Feature',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.12.0',
    resolvedInVersion: '1.12.0',
    reportedDate: '2026-08-23',
    resolvedDate: '2026-08-23',
  },
  {
    id: 'FEAT-036',
    title: 'Karte: Bereichsauswahl für Sammel-Aktionen (gefällt / löschen)',
    description: 'Kreis-Auswahl auf der Karte mit Radius-Slider und Live-Zähler. Enthaltene Bäume gesammelt als gefällt markieren (Baumnummer → GF-, Bemerkung → „ehemalig", Fälldatum) oder unwiderruflich löschen (doppelte Sicherung inkl. Tippen von LÖSCHEN). Für admin + erfasser.',
    area: 'Karte',
    category: 'Feature',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.12.0',
    resolvedInVersion: '1.12.0',
    reportedDate: '2026-08-23',
    resolvedDate: '2026-08-23',
  },
  {
    id: 'IMP-021',
    title: 'Bäume löschen: erfasser erlauben + Dashboard-Layout',
    description: 'Die Baum-Löschroute erlaubt jetzt admin + erfasser (für die Bereich-Löschfunktion; gilt für alle Baum-Löschungen). Dashboard-Kacheln vier nebeneinander.',
    area: 'Rechte / Dashboard',
    category: 'Verbesserung',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.12.0',
    resolvedInVersion: '1.12.0',
    reportedDate: '2026-08-23',
    resolvedDate: '2026-08-23',
  },
  {
    id: 'FEAT-034',
    title: 'Karte: Marker nach Eigentümer einfärben',
    description: 'Optionaler Schalter in der Karten-Legende: färbt die Baum-Marker nach Eigentümer (jeder Eigentümer eine Farbe aus fester Palette) und zeigt eine Eigentümer-Legende. Unabhängig vom Zustand-Farbmodus. Map-Endpoint liefert dafür jetzt eigentuemer mit.',
    area: 'Karte',
    category: 'Feature',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.11.0',
    resolvedInVersion: '1.11.0',
    reportedDate: '2026-08-21',
    resolvedDate: '2026-08-21',
  },
  {
    id: 'FEAT-033',
    title: 'Dashboard: Handlungsbedarf-Überblick',
    description: 'Kachelreihe „Handlungsbedarf" oben im Dashboard: nicht verkehrssicher, Gefahr im Verzug, überfällige Kontrollen, offene Maßnahmen – jeweils mit Anzahl und Klick in die gefilterte Baumliste. Backend-Counts im /reports/dashboard-Endpoint ergänzt.',
    area: 'Dashboard / Auswertung',
    category: 'Feature',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.11.0',
    resolvedInVersion: '1.11.0',
    reportedDate: '2026-08-21',
    resolvedDate: '2026-08-21',
  },
  {
    id: 'IMP-020',
    title: 'Baumkataster: Mehrfachauswahl über Seiten hinweg',
    description: 'Die Auswahl bleibt beim Blättern erhalten (Map statt Set). „In LV" und „Export" nutzen jetzt alle seitenübergreifend ausgewählten Bäume statt nur die aktuelle Seite; der Zähler ist global.',
    area: 'Baumkataster',
    category: 'Verbesserung',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.11.0',
    resolvedInVersion: '1.11.0',
    reportedDate: '2026-08-21',
    resolvedDate: '2026-08-21',
  },
  {
    id: 'BUG-030',
    title: 'Baumkataster: Auswahl-Leiste verdeckt Pagination',
    description: 'Bei aktiver Mehrfachauswahl lag die fixierte Aktions-Leiste über der Seiten-Navigation. Ein Abstandhalter reserviert jetzt Platz, sodass die Pagination sichtbar bleibt.',
    area: 'Baumkataster',
    category: 'Bug',
    priority: 'niedrig',
    status: 'erledigt',
    targetVersion: '1.11.0',
    resolvedInVersion: '1.11.0',
    reportedDate: '2026-08-21',
    resolvedDate: '2026-08-21',
  },
  {
    id: 'IMP-018',
    title: 'Leistungsverzeichnis: Suchmaske für die Maßnahmen-Auswahl',
    description: 'Suchfeld über der Auswahl-Tabelle, filtert nach Baum-Nr., Baumart, Straße und Maßnahmentyp.',
    area: 'Leistungsverzeichnis',
    category: 'Verbesserung',
    priority: 'niedrig',
    status: 'erledigt',
    targetVersion: '1.10.0',
    resolvedInVersion: '1.10.0',
    reportedDate: '2026-07-29',
    resolvedDate: '2026-07-29',
  },
  {
    id: 'IMP-019',
    title: 'Leistungsverzeichnis: Standard-Leistungstexte je Maßnahmentyp',
    description: 'Optionaler Schalter: übernimmt die hinterlegte Standard-Beschreibung (getMeasureBeschreibung) je Maßnahmentyp als Positionsbeschreibung statt der Einzel-Bemerkung.',
    area: 'Leistungsverzeichnis',
    category: 'Verbesserung',
    priority: 'niedrig',
    status: 'erledigt',
    targetVersion: '1.10.0',
    resolvedInVersion: '1.10.0',
    reportedDate: '2026-07-29',
    resolvedDate: '2026-07-29',
  },
  {
    id: 'FEAT-032',
    title: 'Leistungsverzeichnis: Maßnahmen pro Baum bündeln',
    description: 'Optionaler Schalter im LV-Editor: Maßnahmen eines Baums werden zu einer Position zusammengefasst (Typen + Beschreibungen verkettet), ein Preis je Baum. Verhindert doppelte Berechnung. Beim LV-Abschluss werden alle gebündelten Maßnahmen erledigt. Neue Spalte lv_positionen.measure_ids.',
    area: 'Leistungsverzeichnis',
    category: 'Feature',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.10.0',
    resolvedInVersion: '1.10.0',
    reportedDate: '2026-07-29',
    resolvedDate: '2026-07-29',
  },
  {
    id: 'IMP-015',
    title: 'Leistungsverzeichnis: Maßnahmen-Auswahl sortierbar',
    description: 'Klick auf die Spaltenköpfe sortiert die Auswahl-Tabelle (Baum-Nr. numerisch, Baumart, Straße, Maßnahme, Status, geplant/erledigt).',
    area: 'Leistungsverzeichnis',
    category: 'Verbesserung',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.10.0',
    resolvedInVersion: '1.10.0',
    reportedDate: '2026-07-29',
    resolvedDate: '2026-07-29',
  },
  {
    id: 'IMP-016',
    title: 'Maßnahmen löschen: Erfasser erlauben',
    description: 'Die DELETE-Route für Maßnahmen war auf admin beschränkt. Jetzt dürfen admin und erfasser löschen (analog zu Anlegen/Bearbeiten).',
    area: 'Maßnahmen / Rechte',
    category: 'Verbesserung',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.10.0',
    resolvedInVersion: '1.10.0',
    reportedDate: '2026-07-29',
    resolvedDate: '2026-07-29',
  },
  {
    id: 'IMP-017',
    title: 'Auswertung: Gesamtkosten aller LVs anzeigen',
    description: 'Im Maßnahmen-Überblick wird die Summe aller LV-Gesamtkosten als Kennzahl gezeigt – aussagekräftig auch bei Pauschal-LVs, wo keine Kosten je Maßnahme vorliegen.',
    area: 'Auswertung',
    category: 'Verbesserung',
    priority: 'niedrig',
    status: 'erledigt',
    targetVersion: '1.10.0',
    resolvedInVersion: '1.10.0',
    reportedDate: '2026-07-29',
    resolvedDate: '2026-07-29',
  },
  {
    id: 'BUG-029',
    title: 'Maßnahmen-Auswertung zeigt 0 € / keine Kosten',
    description: 'Die Maßnahmen-Auswertung summierte nur die Kosten erledigter Maßnahmen aus tree_measures.cost, die aber meist leer sind (Kosten liegen in den LV-Positionen). Jetzt erben Maßnahmen die Kosten ihrer verknüpften LV-Position, und gezählt werden erledigte ODER kostenbehaftete Maßnahmen.',
    area: 'Auswertung',
    category: 'Bug',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.9.4',
    resolvedInVersion: '1.9.4',
    reportedDate: '2026-06-30',
    resolvedDate: '2026-06-30',
  },
  {
    id: 'FEAT-031',
    title: 'Passwort ändern (Self-Service)',
    description: 'Eingeloggte Nutzer können ihr eigenes Passwort im Avatar-Menü ändern (aktuelles Passwort zur Bestätigung, neues Passwort min. 8 Zeichen). Nutzt die bestehende Route PUT /api/auth/change-password.',
    area: 'Konto / Sicherheit',
    category: 'Feature',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.9.4',
    resolvedInVersion: '1.9.4',
    reportedDate: '2026-06-30',
    resolvedDate: '2026-06-30',
  },
  {
    id: 'BUG-028',
    title: 'LV: Kosten werden bei Statuswechsel auf „abgeschlossen" auf 0 gesetzt',
    description: 'Beim Setzen eines Leistungsverzeichnisses auf „abgeschlossen" (bzw. beim Speichern mit leerem Pauschalfeld) wurden die Gesamtkosten auf 0 zurückgesetzt. Backend erhält die Kosten jetzt und rechnet nur bei echten neuen Daten neu; Frontend sendet bei leerem Pauschalfeld null statt 0.',
    area: 'Leistungsverzeichnis',
    category: 'Bug',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.9.4',
    resolvedInVersion: '1.9.4',
    reportedDate: '2026-06-30',
    resolvedDate: '2026-06-30',
  },
  {
    id: 'IMP-014',
    title: 'Erfasser dürfen Leistungsverzeichnisse löschen',
    description: 'Die DELETE-Route für LVs war auf admin beschränkt, obwohl erfasser LVs anlegen und bearbeiten dürfen. Jetzt konsistent: admin und erfasser dürfen löschen.',
    area: 'Leistungsverzeichnis / Rechte',
    category: 'Verbesserung',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.9.4',
    resolvedInVersion: '1.9.4',
    reportedDate: '2026-06-30',
    resolvedDate: '2026-06-30',
  },
  // ─────────────────────────────────────────────────────────────────────────
  // v1.9.0 – IN ENTWICKLUNG
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'FEAT-026',
    title: 'Verkehrssicherheit am Baum + Ergebnis der Baumkontrolle',
    description: 'Feld „Lebenserwartung" im Baumdetail wird vollständig durch „Verkehrssicherheit" ersetzt (Lebenserwartung entfällt). Werte: Gegeben / Zweifelhaft / Nicht gegeben / Wiederherstellbar. Der Wert ist im Baumdetail manuell editierbar UND wird automatisch gesetzt, wenn unten in der Ergebnis-Box einer Baumkontrolle der Gesamt-Status gewählt und die Kontrolle gespeichert wird. Farbliche Hervorhebung im Baumdetail: grün = gegeben, gelb = zweifelhaft/wiederherstellbar, rot = nicht gegeben. DB: neue Spalte verkehrssicherheit in trees (Lebenserwartung-Spalte wird im UI entfernt), Ergebnis-Spalte in tree_controls.',
    area: 'Baumdetail / Baumkontrolle',
    category: 'Feature',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.9.0',
    resolvedInVersion: '1.9.0',
    reportedDate: '2026-06-30',
    resolvedDate: '2026-06-30',
  },
  {
    id: 'FEAT-027',
    title: 'Letzte & nächste Kontrolle im Baumdetail anzeigen',
    description: 'Im Baumdetail „Letzte Kontrolle" (Datum der jüngsten tree_controls-Zeile) und „Nächste Kontrolle" (letztes Kontrolldatum + kontroll_intervall) anzeigen. Backend berechnet das nächste Datum bereits (vgl. Überfällige-Kontrollen-Alert, BUG-026) – vermutlich überwiegend Anzeige-Ergänzung im Baumdetail.',
    area: 'Baumdetail',
    category: 'Feature',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.9.0',
    resolvedInVersion: '1.9.0',
    reportedDate: '2026-06-30',
    resolvedDate: '2026-06-30',
  },
  {
    id: 'IMP-012',
    title: 'Baumkataster: Filter nach Maßnahmen-Art',
    description: 'Im Baumkataster filterbar nach Maßnahmen-Art (z.B. „Totholzentfernung"), um alle Bäume mit einer bestimmten offenen Maßnahme in einem Ort auf einen Schlag zu finden. Kombinierbar mit Ort/Status. Nutzt vorhandene tree_measures-Felder (Art + Status) – zusätzlicher Query-Parameter + UI-Filter, keine DB-Änderung erwartet. Idee: Ergebnis als Arbeitsliste (PDF/CSV) für die Kolonne exportierbar.',
    area: 'Baumkataster',
    category: 'Verbesserung',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.9.0',
    resolvedInVersion: '1.9.0',
    reportedDate: '2026-06-30',
    resolvedDate: '2026-06-30',
  },
  {
    id: 'FEAT-028',
    title: 'CSV-Import: Verkehrssicherheit + Kontrolldaten (Wanzleben)',
    description: 'Original-CSV (Wanzleben; semikolon-getrennt, 41 Spalten) enthält: Spalte „Verkehrssicherheit" (Werte: verkehrssicher / nicht verkehrssicher / wiederherstellbar / zweifelhaft), „Datum letzte Kontrolle" und „Datum nächste Kontrolle" (beide ISO JJJJ-MM-TT), „Kontrolleur" und „Kontrolle Kommentar". Import: Verkehrssicherheit → trees.verkehrssicherheit (Mapping: verkehrssicher→Gegeben, nicht verkehrssicher→Nicht gegeben, wiederherstellbar→Wiederherstellbar, zweifelhaft→Zweifelhaft). Datum nächste Kontrolle wird direkt aus der CSV übernommen (nicht aus Intervall berechnet). Kontrolldaten als echte tree_controls-Zeile anlegen (Datum, Kontrolleur, Kommentar, Ergebnis=Verkehrssicherheit). Setzt FEAT-026 und FEAT-027 voraus.',
    area: 'CSV-Import',
    category: 'Feature',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.9.1',
    resolvedInVersion: '1.9.1',
    reportedDate: '2026-06-30',
    resolvedDate: '2026-06-30',
  },
  {
    id: 'BUG-027',
    title: 'Kontrolle: „Baum bearbeiten" springt nicht zurück in die Kontrolle',
    description: 'Wenn man während einer Kontrolle „Baum bearbeiten" öffnet (z.B. um das Kontroll-Intervall zu ändern), landet man nach dem Speichern nicht wieder in der Kontrolle, sondern im Baumdetail/der Liste. Fix: Rücksprungziel beim Navigieren merken (state/Query-Param) und nach dem Speichern dorthin zurückkehren. Reiner Frontend-Routing-Fix.',
    area: 'Baumkontrolle / Routing',
    category: 'Bug',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.9.0',
    resolvedInVersion: '1.9.0',
    reportedDate: '2026-06-30',
    resolvedDate: '2026-06-30',
  },
  {
    id: 'FEAT-030',
    title: 'Maßnahmen nachträglich bearbeiten',
    description: 'Bestehende Maßnahmen am Baum über einen Bearbeiten-Dialog ändern (Typ, Beschreibung, Status, geplantes/erledigtes Datum, Bearbeiter, Kosten, Abschlussnotizen). U.a. nötig, um auto-erzeugte Maßnahmen ohne Zuweisung/Datum nachzupflegen.',
    area: 'Baumdetail / Maßnahmen',
    category: 'Feature',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.9.2',
    resolvedInVersion: '1.9.2',
    reportedDate: '2026-06-30',
    resolvedDate: '2026-06-30',
  },
  {
    id: 'FEAT-029',
    title: 'Kontrolle „nicht verkehrssicher" → Maßnahme/Benachrichtigung vorschlagen',
    description: 'Fällt eine Baumkontrolle „nicht verkehrssicher" aus, direkt anbieten, eine Maßnahme anzulegen (Bemerkung übernehmen) und/oder eine Benachrichtigung erzeugen. Analog zum bestehenden Verhalten bei Flächen-Kontrollen (v1.8.0). Setzt FEAT-026 voraus.',
    area: 'Baumkontrolle',
    category: 'Feature',
    priority: 'niedrig',
    status: 'erledigt',
    targetVersion: '1.9.3',
    resolvedInVersion: '1.9.3',
    reportedDate: '2026-06-30',
    resolvedDate: '2026-06-30',
  },
  {
    id: 'IMP-013',
    title: 'Verkehrssicherheit als Filter/Badge im Baumkataster',
    description: 'Bäume nach Verkehrssicherheit filtern (Gegeben / Nicht gegeben / Wiederherstellbar) und als farbiges Badge in Tabelle/Karte anzeigen. Ergänzt FEAT-026 und IMP-012.',
    area: 'Baumkataster',
    category: 'Verbesserung',
    priority: 'niedrig',
    status: 'erledigt',
    targetVersion: '1.9.3',
    resolvedInVersion: '1.9.3',
    reportedDate: '2026-06-30',
    resolvedDate: '2026-06-30',
  },

  // ─────────────────────────────────────────────────────────────────────────
  // v1.6.4 – RELEASED
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'BUG-026',
    title: 'Dashboard: Klick auf „Überfällige Kontrollen"-Alert filtert nicht korrekt',
    description: 'Der „Alle anzeigen"-Button im orangenen Alert „X Bäume haben überfällige Kontrollen!" hat einfach auf /trees navigiert ohne Filter – im Baumkataster sah man dann alle Bäume statt nur die überfälligen. Fix: Der Button setzt jetzt den Filter kontrollenStatus=overdue. Zusätzlich ist das gesamte Alert klickbar (Cursor + Hover-Highlight), sodass auch ein Klick auf den Text funktioniert.',
    area: 'Dashboard',
    category: 'Bug',
    priority: 'niedrig',
    status: 'erledigt',
    targetVersion: '1.6.4',
    resolvedInVersion: '1.6.4',
    reportedDate: '2026-06-03',
    resolvedDate: '2026-06-03',
  },
  {
    id: 'FEAT-025',
    title: 'LV-Abschluss: Kosten in Maßnahmen zurückspielen + Auswertung greift',
    description: 'Bisher landeten die LV-Positions-Kosten nirgendwo in den Maßnahmen-Datensätzen – die Maßnahmen-Auswertung (Tab "Maßnahmen") konnte daher keine echten Kosten ausweisen. Neu: Beim Wechsel des LV-Status auf "abgeschlossen" (Übergang von einem anderen Status) werden alle Positionen mit verknüpfter measure_id automatisch synchronisiert: lv_positionen.kosten → tree_measures.cost, tree_measures.status → "erledigt", completed_date → heute (sofern noch nicht gesetzt). Maßnahmen, die schon erledigt oder abgebrochen sind, bleiben unberührt. Damit funktionieren alle bestehenden Auswertungen (Kosten pro Monat, pro Bearbeiter, pro Ort, pro Maßnahmen-Typ) ohne weitere Datenpflege – die LV-Position ist die Single Source of Truth für Kosten, und der LV-Abschluss ist der Sync-Trigger. Audit-Log-Aktion "LV_COMPLETED" mit Anzahl synchronisierter Maßnahmen.',
    area: 'Auswertung / LV',
    category: 'Feature',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.6.4',
    resolvedInVersion: '1.6.4',
    reportedDate: '2026-06-03',
    resolvedDate: '2026-06-03',
  },

  // ─────────────────────────────────────────────────────────────────────────
  // v1.6.3 – RELEASED 2026-06-03
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'BUG-023',
    title: 'CSV-Import: Schäden werden als echte tree_damages-Einträge angelegt',
    description: 'Bisher landeten Werte aus den CSV-Spalten schaden_1..schaden_5 nur als Text-Strings in trees.scha_o_1..schad_o_5 – sie erschienen nicht im Schäden-Block des Baumblatts. Jetzt werden zusätzlich echte tree_damages-Einträge angelegt: Format-Erwartung "Bereich: Schadmerkmal-Beschreibung" (z.B. "Krone: Totholz mit mehr als 3 cm Durchmesser"). Der Parser erkennt Krone/Stamm/Stammfuß/Stammfuss/Wurzel/Wurzeln/Baumumfeld (case-insensitive) und nimmt den Rest 1:1 als damage_type. Optionale CSV-Spalten prioritaet_1..5 (niedrig/mittel/hoch/dringend) und schweregrad_1..5 (Gering/Mittel/Hoch/Sehr hoch) werden ausgewertet, sonst Default (mittel/Mittel). Re-Import-Schutz: bestehende offene Schäden mit gleichem Bereich + Text werden nicht doppelt angelegt. Werte ohne erkennbaren Bereich (z.B. "Totholz" ohne Doppelpunkt) erzeugen eine Warnung und bleiben nur in scha_o_*.',
    area: 'CSV-Import',
    category: 'Feature',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.6.3',
    resolvedInVersion: '1.6.3',
    reportedDate: '2026-06-03',
    resolvedDate: '2026-06-03',
  },
  {
    id: 'BUG-022',
    title: 'Funktion-Liste um „Einzelbaum" und „öffentliche Einrichtung" erweitert',
    description: 'Die FUNKTION_OPTIONS (DB-Spalte grunflache, Anzeige "Funktion") sind im Baumblatt um zwei häufig benötigte Werte erweitert: "Einzelbaum" (Solitärbaum/freistehender Baum) und "öffentliche Einrichtung" (Rathaus, Bibliothek, Museum, Verwaltung etc.). Der CSV-Import wurde entsprechend angepasst (backend/utils/fuzzy.ts → grunflache options + aliasen): einzeln/einzel/einzelstand/einzelstandort/solitaer/Solitär werden auf "Einzelbaum" gemappt, oeffentlich/verwaltung/rathaus/amt/bibliothek/museum auf "öffentliche Einrichtung". Schadens-Spalten im CSV-Import wurden bewusst NICHT angepasst – Schäden werden weiterhin als Strings in scha_o_1..schad_o_5 importiert (Bestandsschutz).',
    area: 'Stammdaten',
    category: 'Verbesserung',
    priority: 'niedrig',
    status: 'erledigt',
    targetVersion: '1.6.3',
    resolvedInVersion: '1.6.3',
    reportedDate: '2026-06-03',
    resolvedDate: '2026-06-03',
  },
  {
    id: 'BUG-021',
    title: 'FLL-Beschreibungen auch bei Schäden und Maßnahmen',
    description: 'Das Auto-Fill-Verhalten aus BUG-019 (Kontrolle: Schadmerkmal auswählen → FLL-Beschreibung wird in Freitext eingefügt + Beschreibung als grauer Hilfetext im Dropdown) wurde jetzt auch konsistent in Schaden-Dialog und Maßnahmen-Dialog umgesetzt. Maßnahmentypen haben jetzt ebenfalls Beschreibungen (z.B. "Einkürzung von Ästen: xx-Seite um xx m"), die im Dropdown sichtbar sind und beim Auswählen automatisch in die Beschreibung übernommen werden. Beim Wechsel zwischen Typen wird die vorherige Auto-Fill-Beschreibung erkannt und ersetzt – manuell eingegebener Text bleibt unangetastet. Hilfsfunktionen getMerkmalBeschreibung + getMeasureBeschreibung in treeFieldOptions.ts kapseln die Logik.',
    area: 'FLL-Konformität',
    category: 'Verbesserung',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.6.3',
    resolvedInVersion: '1.6.3',
    reportedDate: '2026-06-03',
    resolvedDate: '2026-06-03',
  },
  {
    id: 'BUG-020',
    title: 'Tabelle tree_controls fehlende FLL-Spalten',
    description: 'Beim Anlegen einer Kontrolle kam ein 500-Fehler: "column entwicklungsphase of relation tree_controls does not exist". Der Backend-Code erwartete 15 Spalten (entwicklungsphase, vitalitaetsstufe, zustand, kontroll_intervall, sicherheitserwartung, baumhoehe, stammdurchmesser, kronendurchmesser, krone_befund, krone_verkehr, stamm_befund, stamm_verkehr, wurzeln_befund, wurzeln_verkehr, handlungsbedarf, updated_at), die DB hatte aber nur 8. Migration fügt alle fehlenden Spalten als NULLABLE hinzu – bestehende Kontroll-Einträge bleiben gültig.',
    area: 'Datenbank',
    category: 'Bug',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.6.3',
    resolvedInVersion: '1.6.3',
    reportedDate: '2026-06-03',
    resolvedDate: '2026-06-03',
  },
  {
    id: 'BUG-019',
    title: 'Schadmerkmale mit Auto-Fill der FLL-Beschreibung',
    description: 'Die FLL-Schadmerkmale (Krone/Stamm/Stammfuß) hatten bisher nur Kurz-Werte – die offiziellen ausführlichen Beschreibungen (z.B. "Totholz mit mehr als 3 cm Durchmesser" oder "Astungswunde in einer Höhe von xx m auf xx-Seite, xx cm sondierbar") fehlten. Jetzt werden beim Auswählen eines Merkmals zwei Dinge gemacht: 1) Im Dropdown wird die FLL-Beschreibung als kleiner grauer Hilfetext unter dem Wert angezeigt. 2) Beim Auswählen wird die Beschreibung als neue Zeile in das Freitext-Befund-Feld eingefügt (mit Platzhaltern xx wo Details ausgefüllt werden müssen). Beim Entfernen eines Merkmals wird die zugehörige Zeile wieder aus dem Befund-Text gelöscht. Dadurch bleiben alle FLL-Daten in einem Feld und können vom Erfasser direkt mit Höhen, Seiten und Maßen ergänzt werden.',
    area: 'FLL-Konformität',
    category: 'Verbesserung',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.6.3',
    resolvedInVersion: '1.6.3',
    reportedDate: '2026-06-03',
    resolvedDate: '2026-06-03',
  },
  {
    id: 'BUG-018',
    title: 'Maßnahmen + Schadmerkmale FLL-konform',
    description: 'Maßnahmen-Liste und Schadmerkmale jetzt nach FLL-Baumkontrollrichtlinie. Die Maßnahmen-Liste wurde komplett überarbeitet (19 FLL-Maßnahmen wie "Aufbau- und Erziehungsschnitt", "Einkürzung von Ästen", "Kappung auf Resthöhe", "Einbau einer statischen/dynamischen Kronensicherung" etc.) und ist im Dropdown jetzt in zwei Sektionen gegliedert: "FLL-Maßnahmen" oben und "Weitere" unten (Kontrolle, Bewässerung, Düngung etc. – nicht FLL aber praxisnah). Neu sind außerdem drei Schadmerkmale-Listen (Krone, Stamm, Stammfuß/Wurzel/Baumumfeld) mit allen Einträgen aus der FLL-Tabelle. Diese werden als Multi-Select-Dropdown in der Kontroll-Erfassung angeboten – pro Bereich kann man mehrere Merkmale auswählen, der Freitext-Befund bleibt für Detail-Angaben (Höhe, Seite, Maße) erhalten. Im Schadens-Dialog werden die gleichen Listen je nach Schadbereich (Krone/Stamm/Stammfuß) angeboten.',
    area: 'FLL-Konformität',
    category: 'Verbesserung',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.6.3',
    resolvedInVersion: '1.6.3',
    reportedDate: '2026-05-28',
    resolvedDate: '2026-05-28',
  },
  {
    id: 'FEAT-024',
    title: 'Freie Aufgaben ohne Baumzuordnung',
    description: 'Auf der Seite "Meine Aufgaben" gibt es jetzt einen "Neue Aufgabe"-Button. Im Dialog kann optional ein Baum über ein Suchfeld (Autocomplete nach Baumnummer) zugeordnet werden – oder das Feld bleibt leer für eine freie Aufgabe (z.B. "Mit Tiefbauamt sprechen", "Werkzeug kaufen"). Backend: tree_measures.tree_id ist jetzt NULLABLE (Migration), Endpoints akzeptieren tree_id optional. Freie Aufgaben werden in der Liste mit "Freie Aufgabe (kein Baum)" markiert, im Dashboard mit "(frei)". Klick führt bei freien Aufgaben zur Aufgaben-Übersicht statt zum Baum.',
    area: 'Aufgaben',
    category: 'Feature',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.6.3',
    resolvedInVersion: '1.6.3',
    reportedDate: '2026-05-27',
    resolvedDate: '2026-05-27',
  },
  {
    id: 'BUG-016',
    title: 'Zurück aus Baumdetail führt in Baumkataster statt Historie',
    description: 'Beim Klick auf einen Baum in der Historie öffnete sich zwar das Baumdetail, aber der "Zurück"-Button führte immer zum Baumkataster, nicht zurück zur Historie-Liste. Fix: ActivityLog gibt beim navigate() jetzt state: { from: "activity" } mit, TreeDetail erkennt das und springt mit "Zurück zur Historie" zur richtigen Seite.',
    area: 'Navigation',
    category: 'Bug',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.6.3',
    resolvedInVersion: '1.6.3',
    reportedDate: '2026-05-27',
    resolvedDate: '2026-05-27',
  },
  {
    id: 'BUG-015',
    title: 'Alte Baumnummer in Historie nicht in der Listenansicht sichtbar',
    description: 'Wenn man in der Historie nach einer alten Baumnummer (archive_baumnummer, z.B. "GS005") gesucht hat, fand man zwar den Eintrag, sah die alte Nummer aber erst nach Klick auf das Detail. Fix: ActivityLog zeigt die alte Baumnummer jetzt direkt in der Listenzeile als "(vorher #GS005)" wenn vorhanden. Bei aktiver Suche werden Treffer (aktuelle oder alte Baumnummer) gelb hervorgehoben. Backend: audit-log-Endpoint liefert archive_baumnummer mit, Search-Filter durchsucht beide Nummern.',
    area: 'Historie',
    category: 'Verbesserung',
    priority: 'niedrig',
    status: 'erledigt',
    targetVersion: '1.6.3',
    resolvedInVersion: '1.6.3',
    reportedDate: '2026-05-27',
    resolvedDate: '2026-05-27',
  },
  {
    id: 'BUG-014',
    title: 'Maßnahmen-Liste um Kontroll-Maßnahmen erweitert',
    description: 'In der Maßnahmen-Auswahl (Baumblatt + Bulk-Zuweisung im Baumkataster) fehlten drei wichtige Maßnahmentypen für die regelmäßige Baumkontrolle: "Kontrolle", "eingehende Untersuchung" und "Untere Naturschutzbehörde hinzuziehen". Die werden jetzt am Ende der Liste angeboten. Außerdem wurde die Liste in eine zentrale Konstante (MEASURE_TYPE_OPTIONS in treeFieldOptions.ts) ausgelagert – sodass sie an einer Stelle gepflegt wird und automatisch überall greift.',
    area: 'Maßnahmen',
    category: 'Verbesserung',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.6.3',
    resolvedInVersion: '1.6.3',
    reportedDate: '2026-05-27',
    resolvedDate: '2026-05-27',
  },
  {
    id: 'BUG-013',
    title: 'Hochgeladene Fotos wurden nicht angezeigt (404)',
    description: 'Nach dem Foto-Upload wurde das Bild mit 404 nicht geladen. Ursache: In der nginx-Config des Frontend-Containers hatte die Regex-Location für statische Dateien (.png/.jpg/.css/...) Vorrang vor der /uploads/-Prefix-Location – nginx priorisiert Regex-Locations über einfache Prefix-Locations. Dadurch wurde /uploads/photos/foo.png lokal im Frontend-Container gesucht statt ans Backend weitergereicht. Fix: /uploads/-Location mit ^~ versehen, das erzwingt Prefix-Vorrang und schließt die Regex-Prüfung aus. Betrifft nur die Server-Umgebung mit nginx (lokal liefert Express die Uploads direkt).',
    area: 'Foto-Upload',
    category: 'Bug',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.6.3',
    resolvedInVersion: '1.6.3',
    reportedDate: '2026-05-27',
    resolvedDate: '2026-05-27',
  },
  {
    id: 'BUG-012',
    title: 'Foto-Upload schlug mit 500-Fehler fehl',
    description: 'Der POST /api/photos Endpoint erwartete beim INSERT eine Spalte tree_photos.control_id (für die optionale Verknüpfung eines Fotos mit einer Kontroll-Erfassung), aber die Spalte fehlte im DB-Schema. Migration fügt die Spalte als NULLABLE hinzu, mit Foreign Key auf tree_controls(id) ON DELETE SET NULL – damit Fotos beim Löschen der Kontrolle erhalten bleiben (sie hängen ja primär am Baum). Plus Index für die /photos/control/:controlId Lookup-Route.',
    area: 'Foto-Upload',
    category: 'Bug',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.6.3',
    resolvedInVersion: '1.6.3',
    reportedDate: '2026-05-27',
    resolvedDate: '2026-05-27',
  },

  // ─────────────────────────────────────────────────────────────────────────
  // v1.6.2 – RELEASED 2026-05-27
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'FEAT-022',
    title: 'Badge-Counter im Tab-Title und App-Icon',
    description: 'Ungelesene Notifications werden jetzt mehrfach visualisiert: Tab-Title bekommt einen "(3) Baumwächter"-Präfix, das Favicon wird mit einem kleinen roten Punkt + Zahl überlagert (Canvas-gerendert), und auf installierten PWAs auf Desktop und Android wird die native Badging API (navigator.setAppBadge) verwendet damit ein Badge am App-Icon erscheint. Auf iOS 16.4+ funktioniert das auch für installierte PWAs mit Notification-Permission. Der Badge wird synchron zum vorhandenen 60s-Polling der Glocke aktualisiert und auch bei "alle als gelesen" sauber zurückgesetzt.',
    area: 'PWA / Notifications',
    category: 'Feature',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.6.2',
    resolvedInVersion: '1.6.2',
    reportedDate: '2026-05-27',
    resolvedDate: '2026-05-27',
  },
  {
    id: 'FEAT-021',
    title: 'Baumwächter als installierbare PWA',
    description: 'Web-App-Manifest mit Icons (verschiedene Größen + maskable), Theme-Color #4a7c59, display:standalone und Schnellaktionen für Karte/Meine Aufgaben/Baumkataster. Apple-spezifische Meta-Tags (apple-mobile-web-app-capable, apple-mobile-web-app-status-bar-style: black-translucent, viewport-fit=cover für Notch) ermöglichen Vollbildmodus auf iPhones via Safari "Zum Home-Bildschirm". Minimaler Service Worker (frontend/public/service-worker.js) registriert sich in Production, cached die App-Shell (Network-First) und ist vorbereitet für Push-Notifications. Nginx-Config erweitert: service-worker.js mit no-cache + Service-Worker-Allowed Root-Scope. Registrierung via frontend/src/serviceWorkerRegistration.ts, aufgerufen aus index.tsx.',
    area: 'PWA',
    category: 'Feature',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.6.2',
    resolvedInVersion: '1.6.2',
    reportedDate: '2026-05-27',
    resolvedDate: '2026-05-27',
  },

  // ─────────────────────────────────────────────────────────────────────────
  // v1.6.1 – RELEASED 2026-05-21
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'FEAT-020',
    title: 'CSV-Import mit Fuzzy-Match für Dropdown-Felder & Baumgruppen',
    description: 'Der CSV-Import wurde an die aktuellen Baumblatt-Felder angeglichen. Neu: "Funktion" als Spalten-Alias für Grünfläche, plus die Baumgruppen-Felder (Baumgruppe ja/nein, Gruppe_Anzahl, Gruppe_Arten – kommasepariert wird zu text[]). Für alle strukturierten Dropdown-Felder (Zustand, Vitalität, Entwicklung, Sicherheit, Lebenserwartung, Funktion, Kontroll-Intervall) gibt es jetzt Fuzzy-Match: Schreibvarianten und Aliase werden automatisch auf die kanonischen Werte gemappt (z.B. "Strasse" → "Straßenbegleitgrün", "leicht geschädigt" → "leichte Mängel", "jaehrlich" → "jährlich"). Werte ohne Treffer fallen bei der Funktion auf "Sonstiges" zurück, in den anderen Feldern werden sie 1:1 durchgereicht (DB-flexibel). Nach dem Import zeigt das Frontend einen Auto-Mappings-Report mit Häufigkeit, sortiert nach Typ – "Sonstiges"-Fallbacks oben damit der User sie prüfen kann. Plus aktualisierte CSV-Vorlage mit allen Feldern.',
    area: 'Datenimport',
    category: 'Verbesserung',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.6.1',
    resolvedInVersion: '1.6.1',
    reportedDate: '2026-05-21',
    resolvedDate: '2026-05-21',
  },

  // ─────────────────────────────────────────────────────────────────────────
  // v1.6.0 – RELEASED 2026-05-20
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'FEAT-019',
    title: 'Sidebar gruppiert mit einklappbaren Bereichen',
    description: 'Die Sidebar ist jetzt in vier logische Gruppen unterteilt mit Überschriften: "Überblick" (Dashboard, Meine Aufgaben), "Baumdaten" (Karte, Baumkataster, Datenimport), "Planung & Reports" (Leistungsverzeichnis, Auswertung) und "Verwaltung" (Benutzer, Historie, Release Notes). Jede Gruppe kann per Klick auf die Überschrift einzeln ein- und ausgeklappt werden. Der Toggle-Zustand bleibt im Browser gespeichert (localStorage). Default sind alle Gruppen offen.',
    area: 'Navigation',
    category: 'Verbesserung',
    priority: 'niedrig',
    status: 'erledigt',
    targetVersion: '1.6.0',
    resolvedInVersion: '1.6.0',
    reportedDate: '2026-05-20',
    resolvedDate: '2026-05-20',
  },
  {
    id: 'FEAT-018',
    title: 'PDF-Auswertungen für Maßnahmen und LV',
    description: 'Auf der Auswertungs-Seite gibt es jetzt einen PDF-Export-Button für die Maßnahmen-Auswertung (berücksichtigt den eingestellten Zeitraum-Filter) sowie ein PDF-Icon in jeder Leistungsverzeichnis-Zeile. Die PDFs enthalten Header mit Branding und Filter-Info, Stat-Cards mit Kennzahlen, Top-Listen (Maßnahmentypen, Orte) und eine Detail-Tabelle mit allen Einträgen. Backend nutzt pdfkit, sauberes A4-Layout mit Seitenzahlen-Footer.',
    area: 'Auswertungen',
    category: 'Feature',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.6.0',
    resolvedInVersion: '1.6.0',
    reportedDate: '2026-05-20',
    resolvedDate: '2026-05-20',
  },
  {
    id: 'FEAT-017',
    title: 'Baumblatt vereinheitlicht & "Grünfläche" → "Funktion"',
    description: 'Anzeige und Edit-Form im Baumblatt zeigen jetzt dieselben Felder in derselben Reihenfolge, gegliedert in drei Sektionen: "Identität & Standort", "Bewertung" und "Maße & Daten". Edit-Form komplett neu strukturiert. "Grünfläche" wurde überall in "Funktion" umbenannt und ist jetzt ein Dropdown mit den Werten Allee, Friedhof, Kindergarten, Park, Schule, Spielplatz, Sportplatz, Straßenbegleitgrün, Sonstiges (statt Freitext). DB-Feld bleibt grunflache für Backwards-Compat zur alten App. Funktion-Dropdown auch im MapView-Erfassdialog verfügbar.',
    area: 'Baumdaten',
    category: 'Verbesserung',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.6.0',
    resolvedInVersion: '1.6.0',
    reportedDate: '2026-05-20',
    resolvedDate: '2026-05-20',
  },

  // ─────────────────────────────────────────────────────────────────────────
  // v1.6.0 – früher in Paket 1 (UX-Feinschliff)
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'FEAT-016',
    title: 'UX-Feinschliff & Dashboard-Drill-Down (Paket 1)',
    description: 'Dashboard: "Meine Aufgaben"-Box auf volle Breite verschoben, direkt unter den Stat-Cards. Alle Stat-Cards verlinken jetzt mit gesetzten Filtern in den Baumkataster (z.B. "Offene Schäden" → Trees-Liste mit schaedenStatus=open). Baumkataster: Bei Baumgruppen wird statt der Arten-Liste nur "Gruppe · X Bäume" angezeigt – Details im Baumblatt sichtbar. Schäden-Badge im Baumblatt zählt nur noch offene (in Bearbeitung) Schäden, nicht mehr behobene. Backend-Filter angepasst (status NOT IN erledigt,behoben). Karte: Fehlermeldungen bei doppelter Baumnummer erscheinen jetzt im Erfass-Dialog statt dahinter. Kontroll-Dialog: TextField-Labels mit shrink:true, Placeholder zeigt Beispielwerte statt das Label zu überlappen. Sidebar-Reihenfolge optimiert (Workflow-Tools oben, Verwaltung unten). "Aktivitätenlog" → "Historie" umbenannt.',
    area: 'UX / Dashboard',
    category: 'Verbesserung',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.6.0',
    resolvedInVersion: '1.6.0',
    reportedDate: '2026-05-20',
    resolvedDate: '2026-05-20',
  },

  // ─────────────────────────────────────────────────────────────────────────
  // v1.5.0 – RELEASED 2026-05-19
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'FEAT-015',
    title: 'Baumgruppen-Erfassung',
    description: 'Toggle "Das ist eine Baumgruppe" im Erfass- und Edit-Dialog. Bei aktivem Toggle erscheinen die Felder "Anzahl Bäume" (Pflichtfeld, mindestens 2) und "Arten" (Mehrfachauswahl mit Autocomplete aus vorhandenen Baumarten). Bei Gruppen wird das normale Baumart-Feld ausgeblendet und automatisch aus den gewählten Arten als kommaseparierte Liste befüllt (damit Suche/Filter weiter funktionieren). Eigenes Karten-Symbol: drei überlappende Kreise statt einzelnem Punkt, mit Zustands-Farbe und Kontroll-Status-Indikator innen. Im Baumblatt-Kopf erscheint ein Chip "Gruppe · X Bäume" und eine Zeile mit allen Arten. Backend: trees.is_baumgruppe (bool), trees.gruppe_anzahl (int), trees.gruppe_arten (text[]).',
    area: 'Baumdaten / Karte',
    category: 'Feature',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.5.0',
    resolvedInVersion: '1.5.0',
    reportedDate: '2026-05-19',
    resolvedDate: '2026-05-19',
  },
  {
    id: 'FEAT-014',
    title: 'Gefällte Bäume mit GF-Nummer + Kontroll-Intervall Cleanup',
    description: 'Zustand "gefällt" als Dropdown-Option ergänzt. Beim Setzen wird die alte Baumnummer ins Archiv verschoben (Anzeige im Baumblatt: "Ursprünglich: B-123") und durch eine fortlaufende GF-Nummer (GF000001, GF000002, ...) ersetzt. Die freie Originalnummer kann sofort für einen neu gepflanzten Baum verwendet werden. Eigenes Karten-Symbol (Baumstumpf in Holzbraun). Filter "Gefällte anzeigen" (default: aus) in Baumkataster und Karte. Backend: trees.archive_baumnummer + Sequence gf_baumnummer_seq + Helper next_gf_baumnummer(). Außerdem: Kontroll-Intervall im Kontroll-Dialog jetzt read-only mit Link "Am Baum bearbeiten" (Single Source of Truth: trees.kontroll_intervall).',
    area: 'Baumdaten / Karte',
    category: 'Feature',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.5.0',
    resolvedInVersion: '1.5.0',
    reportedDate: '2026-05-18',
    resolvedDate: '2026-05-18',
  },
  {
    id: 'FEAT-013',
    title: 'Verschiedene UX-Verbesserungen (Paket 1)',
    description: 'Dashboard: "Nie kontrolliert"-Stat-Card entfernt (passte nicht ins Layout, Tabelle bleibt erhalten). Zustandsverteilung mit kräftigeren Farben für bessere Unterscheidbarkeit (kräftiges Grün/satter Gelb/dunkles Orange/tiefes Rot/Schwarz/Holzbraun). Aktivitätenlog-Berechtigungen: Admin sieht alles, Erfasser sieht alle außer Login/Logout anderer User, Betrachter sieht nur eigene Aktionen. Karte: Toggle "Farbige Marker" in der Legende (default: aus) – im Aus-Modus werden alle Bäume als einfache dunkelgrüne Punkte angezeigt, was bei vielen Bäumen die Performance deutlich verbessert.',
    area: 'UX',
    category: 'Verbesserung',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.5.0',
    resolvedInVersion: '1.5.0',
    reportedDate: '2026-05-17',
    resolvedDate: '2026-05-17',
  },
  {
    id: 'FEAT-012',
    title: 'Schaden → Maßnahme Workflow + Verknüpfung',
    description: 'Nach Erfassung eines Schadens kann direkt eine Maßnahme dazu angelegt werden ("Maßnahme dazu anlegen"-Button am Schaden-Eintrag, Beschreibung wird vorausgefüllt). Maßnahme und Schaden werden in der DB verknüpft (tree_measures.damage_id). Beide zeigen einen Chip mit Link zur jeweils anderen Seite. Beim Erledigen einer verknüpften Maßnahme wird der User gefragt, ob der Schaden auch als "behoben" markiert werden soll.',
    area: 'Schäden / Maßnahmen',
    category: 'Feature',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.5.0',
    resolvedInVersion: '1.5.0',
    reportedDate: '2026-05-17',
    resolvedDate: '2026-05-17',
  },
  {
    id: 'FEAT-011',
    title: 'Maßnahmen-Zuweisung und In-App-Benachrichtigungen',
    description: 'Maßnahmen können einem Benutzer als Bearbeiter zugewiesen werden (Admins/Erfasser zuweisen jeden, Selbstzuweisung erlaubt). Neuer Status "in Arbeit" zwischen "offen" und "erledigt". Glocken-Icon in der Topbar mit Counter ungelesener Benachrichtigungen. Dropdown zeigt letzte 50 Notifications, "Alle als gelesen"-Aktion. Eigene Seite "Meine Aufgaben" mit allen zugewiesenen offenen Maßnahmen, gruppiert nach Status und mit Quick-Aktionen (Annehmen, Erledigt). Dashboard-Box "Meine Aufgaben" zeigt die wichtigsten Tasks. Ersteller von Schäden und Maßnahmen wird im Baumblatt angezeigt. Backend: assigned_to/by/at in tree_measures, Tabelle notifications, Routes /notifications/*, /measures/my-tasks.',
    area: 'Maßnahmen',
    category: 'Feature',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.5.0',
    resolvedInVersion: '1.5.0',
    reportedDate: '2026-05-13',
    resolvedDate: '2026-05-17',
  },
  {
    id: 'FEAT-010',
    title: 'Kontroll-Intervall-Logik im Dashboard und Baumkataster nutzen',
    description: 'Das neue Feld trees.kontroll_intervall wird jetzt überall berücksichtigt: Dashboard-Statistiken (Überfällig, Bald fällig, Nie kontrolliert), Baumkataster-Filter (Kontroll-Status: aktuell / bald fällig / überfällig / nie). Backend-Logik berechnet effektives Intervall via COALESCE(trees.kontroll_intervall, letzte tree_controls.kontroll_intervall, "jährlich"). Neuer Endpoint /reports/never-controlled. Neue Dashboard-Box und Stat-Card "Nie kontrolliert". Filter-Chip-Gruppe von 3 auf 5 Optionen erweitert.',
    area: 'Dashboard',
    category: 'Feature',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.5.0',
    resolvedInVersion: '1.5.0',
    reportedDate: '2026-05-13',
    resolvedDate: '2026-05-13',
  },
  {
    id: 'BUG-010',
    title: 'Karte auf Mobile unten abgeschnitten – Neuer-Baum-Button nicht erreichbar',
    description: 'Mobile-Höhe der Karte berücksichtigt jetzt die Bottom-Navigation (calc(100vh - 232px) statt 152px). Der "Neuer Baum"-Button ist wieder voll erreichbar.',
    area: 'Karte',
    category: 'Bug',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.5.0',
    resolvedInVersion: '1.5.0',
    reportedDate: '2026-05-12',
    resolvedDate: '2026-05-12',
  },
  {
    id: 'BUG-011',
    title: 'Filter werden nicht beibehalten beim Zurück-Navigieren',
    description: 'Filter-State der Baumtabelle (Suche, Status, Zustand, Ort, Sortierung) wird jetzt in sessionStorage gecacht. Filter überleben Navigation zu Baumdetail und zurück, sowie Tab-Wechsel innerhalb der Session. Beim Schließen des Browser-Tabs werden sie zurückgesetzt.',
    area: 'Baumkataster',
    category: 'Bug',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.5.0',
    resolvedInVersion: '1.5.0',
    reportedDate: '2026-05-12',
    resolvedDate: '2026-05-12',
  },
  {
    id: 'IMP-009',
    title: 'Autocomplete für Baumart, Ort und Straße',
    description: 'Im Baum-Bearbeiten-Dialog sind die Felder Baumart, Ort und Straße als Autocomplete-Eingaben umgesetzt. Vorschläge kommen aus den bereits in der DB vorhandenen Werten (via /trees/filter-options). Freitext-Eingabe weiterhin möglich (freeSolo), für neue Werte. Reduziert Tippfehler und vereinheitlicht Schreibweisen.',
    area: 'Baumdetail',
    category: 'Verbesserung',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.5.0',
    resolvedInVersion: '1.5.0',
    reportedDate: '2026-05-12',
    resolvedDate: '2026-05-12',
  },
  {
    id: 'IMP-010',
    title: 'Dropdowns für strukturierte Felder in Baum-Bearbeiten und Erfassen',
    description: 'Felder mit definierten Werten (Zustand, Vitalität, Entwicklung, Sicherheit, Lebenserwartung, Kontroll-Intervall, Anzahl Stämme) sind jetzt Dropdowns. Werte aus der alten Baumwächter-App übernommen: Zustand (gut / leichte Mängel / erhebliche Mängel / Gefahr im Verzug), Vitalität (0-3-Skala), Entwicklung (Jugend-/Reife-/Alterungs-/Abgangsphase), Sicherheit (hoch / niedrig), Lebenserwartung (kurz / mittel / lang), Kontroll-Intervall (halbjährlich / jährlich / 2-jährlich / 3-jährlich), Anzahl Stämme (1-4). Eigentümer ist Autocomplete (analog Baumart/Ort/Straße). Im Karten-"Neuer Baum"-Dialog und im Baum-Bearbeiten-Dialog umgesetzt. Wiederverwendbare Konstanten in frontend/src/data/treeFieldOptions.ts. Neues DB-Feld trees.kontroll_intervall via Migration.',
    area: 'Baumdetail',
    category: 'Verbesserung',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.5.0',
    resolvedInVersion: '1.5.0',
    reportedDate: '2026-05-12',
    resolvedDate: '2026-05-13',
  },
  {
    id: 'IMP-011',
    title: 'Schäden-Erfassung auf alte Struktur umstellen (Krone/Stamm/Wurzeln)',
    description: 'Die alte Baumwächter-App strukturiert Schäden anders: pro Baum gibt es drei Bereiche – Krone, Stamm, Wurzeln – jeweils mit Befund (Freitext) und Verkehrssicherheit (gegeben / nicht gegeben / eingeschränkt gegeben). Plus ein zusammenfassendes Feld "Handlungsbedarf". Dies ersetzt die aktuelle Einzel-Schäden-Logik (tree_damages). Größerer Umbau: DB-Migration, Backend-API, UI im Baumblatt. Maria hat entschieden: erst nach v1.5.0 angehen.',
    area: 'Baumdetail',
    category: 'Verbesserung',
    priority: 'mittel',
    status: 'offen',
    targetVersion: '1.6.0',
    reportedDate: '2026-05-12',
  },
  {
    id: 'FEAT-008',
    title: 'Karten-Legende mit klareren Zustand-Symbolen',
    description: 'Marker-Symbole auf der Karte sind jetzt differenziert: gesund / leichte Mängel / erhebliche Mängel als grüner / gelber / oranger Punkt, "Gefahr im Verzug" oder stark geschädigt als gelbes Warnschild mit Ausrufezeichen, abgestorben als schwarzes Dreieck. Macht kritische Bäume auch bei dichter Karte sofort sichtbar.',
    area: 'Karte',
    category: 'Feature',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.5.0',
    resolvedInVersion: '1.5.0',
    reportedDate: '2026-05-12',
    resolvedDate: '2026-05-13',
  },
  {
    id: 'FEAT-009',
    title: 'Kontroll-Status visuell auf der Karte (Doppelfarbe)',
    description: 'Jeder Marker zeigt jetzt zwei Informationen gleichzeitig: außen den Zustand (siehe FEAT-008), innen einen kleinen farbigen Punkt für den Kontroll-Status. Grün = aktuell (mehr als 30 Tage bis nächste Kontrolle), Gelb = bald fällig (nächste 30 Tage), Rot = überfällig oder nie kontrolliert. Berechnung über effektives Intervall (Baum-Level gewinnt, sonst letzte Kontrolle, sonst jährlich). Hilft bei der Planung von Kontroll-Touren auf einen Blick.',
    area: 'Karte',
    category: 'Feature',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.5.0',
    resolvedInVersion: '1.5.0',
    reportedDate: '2026-05-12',
    resolvedDate: '2026-05-13',
  },
  {
    id: 'FEAT-007',
    title: 'Offline-Modus für Felderfassung',
    description: 'Service Worker + IndexedDB: Bäume können auch ohne Netzverbindung erfasst werden (typische Situation im Wald). Erfassungen werden lokal gespeichert und automatisch synchronisiert sobald wieder online. UI-Indikator zeigt aktuellen Verbindungsstatus und Anzahl pendings. Großes Feature und Hauptattraktion von v1.5.0.',
    area: 'App-weit',
    category: 'Feature',
    priority: 'hoch',
    status: 'offen',
    targetVersion: '1.5.0',
    reportedDate: '2026-05-09',
  },

  // ─────────────────────────────────────────────────────────────────────────
  // v1.4.0 – CSV-Import, Bulk-Aktionen, Trends & Offline-Modus
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'IMP-006',
    title: 'CSV-Import an aktuelles Tree-Schema anpassen',
    description: 'Backend komplett auf das echte Schema umgestellt (vorher nicht funktionsfähig). Mapping unterstützt alle relevanten Tree-Felder inkl. Mehrstamm-Daten, Vitalität, Entwicklung, Sicherheit, Lebenserwartung, ND, Habitat, Importschäden, Gefällt-Status. Geometrie aus Lat/Lng (WGS84) oder UTM-33 (Rechtswert/Hochwert) via proj4. Stammumfang → Stammdurchmesser via /π. Bäume ohne Koordinaten: Warnung, aber importiert. CSV-Vorlage neu generiert.',
    area: 'Datenimport',
    category: 'Verbesserung',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.4.0',
    resolvedInVersion: '1.4.0',
    reportedDate: '2026-05-09',
    resolvedDate: '2026-05-09',
  },
  {
    id: 'IMP-007',
    title: 'Empty States mit Icon, Text und CTA-Button',
    description: 'Wiederverwendbare EmptyState-Komponente (frontend/src/components/EmptyState.tsx) mit großem getöntem Icon, Titel, beschreibendem Text und optionalem Call-to-Action-Button. Eingesetzt an: Baumtabelle (Tabellenansicht + Card-View), TreeDetail (Schäden, Maßnahmen, Fotos, Kontrollen), Leistungsverzeichnis (Tab "Gespeicherte LVs") und Aktivitätenlog. Differenziert zwischen "Filter aktiv, keine Treffer" und "noch nie etwas erfasst". Theme-Tokens via alpha(), Dark-Mode-konform.',
    area: 'App-weit',
    category: 'Verbesserung',
    priority: 'niedrig',
    status: 'erledigt',
    targetVersion: '1.4.0',
    resolvedInVersion: '1.4.0',
    reportedDate: '2026-05-09',
    resolvedDate: '2026-05-12',
  },
  {
    id: 'IMP-008',
    title: 'Dashboard mit Trend-Charts statt nur Zahlen',
    description: 'Neuer Backend-Endpoint /reports/trends liefert Zeitreihen der letzten 12 Monate (Bäume erfasst, Schäden eröffnet/behoben, Maßnahmen geplant/erledigt, Kontrollen) inkl. Vergleich Vormonat. Frontend: neue Trend-Sektion im Dashboard mit Sparkline-Charts pro Metrik und Delta-Anzeige (+/− % im Vergleich zum Vormonat). Schäden eröffnet ist farb-invertiert: weniger ist gut. Theme-konform mit AreaChart-Gradienten.',
    area: 'Dashboard',
    category: 'Verbesserung',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.4.0',
    resolvedInVersion: '1.4.0',
    reportedDate: '2026-05-09',
    resolvedDate: '2026-05-12',
  },
  {
    id: 'FEAT-003',
    title: 'Bulk-Aktionen in der Baumtabelle',
    description: 'Floating Action Bar unten am Bildschirmrand mit Pill-Design. Sichtbar sobald mind. 1 Baum ausgewählt. Aktionen: (1) Zustand massenhaft setzen, (2) In bestehendes Leistungsverzeichnis übernehmen (Bäume werden als positions-only Einträge angehängt), (3) Maßnahme zuweisen (bereits vorhanden), (4) Als gefällt markieren (setzt gefaellt=true und gefaellt_a=heute), (5) CSV-Export der Auswahl (clientseitig), (6) Massenlöschung (nur Admin). Mit Master-Checkbox im Tabellen-Header und Indeterminate-State. Auf Mobile über der Bottom-Nav positioniert.',
    area: 'Baumkataster',
    category: 'Feature',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.4.0',
    resolvedInVersion: '1.4.0',
    reportedDate: '2026-05-09',
    resolvedDate: '2026-05-12',
  },
  {
    id: 'FEAT-004',
    title: 'Foto-Direktupload mit Kamera-Fallback',
    description: 'Im Baumblatt zeigt der "Foto hinzufügen"-Button auf Mobile ein Auswahl-Menü "Kamera / Galerie". Kamera-Option nutzt input[type=file] capture="environment" und öffnet direkt die Rückkamera. Auf Desktop bleibt der klassische Datei-Picker. Plus: Button-Icon ist jetzt PhotoCamera für mehr Klarheit.',
    area: 'Baumdetail',
    category: 'Feature',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.4.0',
    resolvedInVersion: '1.4.0',
    reportedDate: '2026-05-09',
    resolvedDate: '2026-05-12',
  },
  {
    id: 'FEAT-005',
    title: 'CSV-Export der Baumtabelle',
    description: 'Neuer Backend-Endpoint /api/trees/export, der dieselben Query-Params wie /api/trees nimmt aber alle gefilterten Treffer ohne Pagination als CSV zurückgibt (kompakter 18-Spalten-Standardexport mit Koordinaten). Plus /api/trees/count für Pre-Export-Anzeige. Backend-Filter-Logik in buildTreesWhere() Helper extrahiert (kein Code-Duplikat). Frontend: Export-Button in der Toolbar öffnet einen Bestätigungs-Dialog der die Anzahl betroffener Bäume zeigt – inkl. Warnung bei großen Exports (>5000).',
    area: 'Baumkataster',
    category: 'Feature',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.4.0',
    resolvedInVersion: '1.4.0',
    reportedDate: '2026-05-09',
    resolvedDate: '2026-05-12',
  },
  {
    id: 'FEAT-006',
    title: 'Audit-Log filtern & exportieren',
    description: 'Aktivitätslog erweitert um Filter: Benutzer-Dropdown, Aktion-Typ, Entität (Baum/Schaden/Massnahme/Foto/Kontrolle/LV/Benutzer/Import), Von-Bis-Datum und Volltext-Suche im Eintrags-Inhalt. Filter im aufklappbaren Panel (wie Baumtabelle). CSV-Export der gefilterten Einträge mit Pre-Export-Bestätigungs-Dialog inkl. Anzahl. Backend: neue Endpoints /audit-log/count, /audit-log/export, /audit-log/filter-options. Filter-Logik in buildAuditLogWhere() Helper extrahiert.',
    area: 'Aktivitätslog',
    category: 'Verbesserung',
    priority: 'niedrig',
    status: 'erledigt',
    targetVersion: '1.4.0',
    resolvedInVersion: '1.4.0',
    reportedDate: '2026-05-09',
    resolvedDate: '2026-05-12',
  },

  // ── Priorität 1 – Kritische Bugs & Sicherheit ─────────────────────────────
  {
    id: 'SEC-001',
    title: 'Schäden & Maßnahmen nur durch Admin löschbar',
    description: 'Das Löschen von Schäden und Maßnahmen am Baum darf nur Admins erlaubt sein. Verifiziert: Backend hat requireRole(\'admin\') auf allen DELETE-Routes, Frontend zeigt Lösch-Buttons nur via {isAdmin && ...}.',
    area: 'Baumdetail',
    category: 'Sicherheit',
    priority: 'kritisch',
    status: 'erledigt',
    targetVersion: '1.3.0',
    resolvedInVersion: '1.3.0',
    reportedDate: '2026-05-07',
    resolvedDate: '2026-05-07',
  },
  {
    id: 'BUG-001',
    title: 'Aktivitätenlog zeigt nichts (bzw. leere Einträge)',
    description: 'Backend las aus tree_changes (alte Tabelle ohne action-Spalte), während alle modernen Aktionen in audit_logs landen. Backend-Route auf audit_logs umgestellt mit Tree- und User-Join. Frontend: erweiterte Aktionsliste, User-Anzeige, defensive Auswertung.',
    area: 'Aktivitätslog',
    category: 'Bug',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.3.0',
    resolvedInVersion: '1.3.0',
    reportedDate: '2026-05-07',
    resolvedDate: '2026-05-07',
  },
  {
    id: 'BUG-002',
    title: 'Maßnahmenauswertung ergibt nichts',
    description: 'Die Auswertung filterte hart auf status="abgeschlossen", während neu in der App erstellte Maßnahmen "erledigt" speichern. Auswertung akzeptiert jetzt beide Werte. Zusätzlich: defensiver Fetch (kein Promise.all-Crash bei Teilfehler) und aussagekräftige Diagnose-Meldungen.',
    area: 'Auswertung',
    category: 'Bug',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.3.0',
    resolvedInVersion: '1.3.0',
    reportedDate: '2026-05-07',
    resolvedDate: '2026-05-07',
  },

  // ── Priorität 2 – UI/UX-Bugs ─────────────────────────────────────────────
  {
    id: 'BUG-003',
    title: 'Filter-Overlay verdeckt Zoom-Buttons auf der Karte',
    description: 'Filter-Button und ausgeklapptes Filter-Panel sitzen oben links und überlappten die Standard-Position der Leaflet-Zoom-Buttons. Lösung: Standard-Zoom deaktiviert, ZoomControl explizit nach bottomleft positioniert. Zusätzlich: bgcolor "white" → "background.paper" auf Filter, Legende, Popup und Hinweis-Box, damit es im Dark-Mode konsistent aussieht.',
    area: 'Karte',
    category: 'Bug',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.3.0',
    resolvedInVersion: '1.3.0',
    reportedDate: '2026-05-07',
    resolvedDate: '2026-05-07',
  },
  {
    id: 'BUG-004',
    title: 'Eingabe-Kasten verdeckt die Karte beim Baum-Anlegen',
    description: 'Beim Klick auf die Karte öffnete sich sofort das Vollformular und überdeckte die Karte. Workflow umgestellt: Klick setzt Pin, Bottom-Bar zeigt Standort + Bestätigen-/Verschieben-Hinweis. Erst auf "Bestätigen" öffnet das Formular. Im Formular "Zurück" schließt nur den Dialog – Pin und Modus bleiben, sodass man neu positionieren kann.',
    area: 'Karte',
    category: 'Bug',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.3.0',
    resolvedInVersion: '1.3.0',
    reportedDate: '2026-05-07',
    resolvedDate: '2026-05-07',
  },
  {
    id: 'BUG-005',
    title: 'Tabelle im Leistungsverzeichnis verschiebt sich',
    description: 'Die Maßnahmen-Card hatte stark unterschiedliche Höhen je nach Zustand (kein Ort gewählt / keine Maßnahmen / Tabelle). Card hat jetzt feste Mindesthöhe (500px), leere Zustände werden zentriert dargestellt, Tabelle ist intern scrollbar mit Sticky-Header (max. 600px sichtbar). Card-Höhe bleibt damit stabil.',
    area: 'Leistungsverzeichnis',
    category: 'Bug',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.3.0',
    resolvedInVersion: '1.3.0',
    reportedDate: '2026-05-07',
    resolvedDate: '2026-05-07',
  },
  {
    id: 'BUG-006',
    title: 'Tabelle "Erledigt" zeigt kein Datum',
    description: 'Erledigt-Datum jetzt an drei Stellen sichtbar: (1) Baumblatt – Maßnahmen-Header zeigt "Geplant: X · Erledigt: Y" nebeneinander, (2) Leistungsverzeichnis Tab 0 – neue Spalte "Erledigt", (3) LV-Detail-Dialog – neue Spalte "Erledigt" mit aktuellem Wert aus tree_measures (Backend GET-Endpoint joint jetzt mit tree_measures, liefert auch aktuellen Status mit). Bonus: Im Baumblatt wird "abgeschlossen" jetzt auch als erledigt erkannt (Importdaten-Toleranz).',
    area: 'Maßnahmen',
    category: 'Bug',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.3.0',
    resolvedInVersion: '1.3.0',
    reportedDate: '2026-05-07',
    resolvedDate: '2026-05-07',
  },
  {
    id: 'BUG-007',
    title: 'LV-Gesamtkosten beim Bearbeiten nicht übernommen',
    description: 'Beim Bearbeiten eines LV im Pauschal-Modus ging der eingegebene Gesamtkosten-Betrag verloren. Ursache: Spalte kosten_modus existierte nicht in der DB, gesamtkosten_manuell wurde im Backend nicht gelesen, und gesamtkosten wurde immer aus den (null-)Positionen-Kosten neu berechnet. Fix: DB-Migration für kosten_modus + Backend liest und persistiert beide Felder, gesamtkosten wird modus-abhängig berechnet.',
    area: 'Leistungsverzeichnis',
    category: 'Bug',
    priority: 'hoch',
    status: 'erledigt',
    targetVersion: '1.3.0',
    resolvedInVersion: '1.3.0',
    reportedDate: '2026-05-07',
    resolvedDate: '2026-05-07',
  },

  // ── Priorität 3 – Workflow-Verbesserungen ────────────────────────────────
  {
    id: 'IMP-001',
    title: 'Neuer Baum: Punkt setzen → Bestätigen → Vollformular',
    description: 'Drei-Stufen-Workflow umgesetzt: 1) Klick auf Karte setzt Pin. 2) Bottom-Bar zeigt Koordinaten und Verschieben-Hinweis – beliebig oft umsetzbar. 3) Bestätigen-Button öffnet das Vollformular. Gemeinsam mit BUG-004 gelöst.',
    area: 'Karte',
    category: 'Verbesserung',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.3.0',
    resolvedInVersion: '1.3.0',
    reportedDate: '2026-05-07',
    resolvedDate: '2026-05-07',
  },
  {
    id: 'IMP-002',
    title: 'Letzte Änderung im Baumblatt prominenter',
    description: 'Aus der grauen Mini-Caption wurde ein outlined Chip mit Uhr-Icon: "Zuletzt geändert vor 3 Tagen · von Maria Müller". Relative Zeitangabe (gestern / vor X Tagen / vor X Wochen), absolutes Datum als Tooltip beim Hover. Header insgesamt aufgeräumt: Top-Toolbar (Zurück + Aktionen) → Title-Block (Baumnummer + Baumart) → Meta-Chips (Letzte Änderung, Schäden, Maßnahmen) in einer Reihe. Klare Hierarchie, Theme-konforme Statusfarben, Plural-/Singular-Logik.',
    area: 'Baumdetail',
    category: 'Verbesserung',
    priority: 'niedrig',
    status: 'erledigt',
    targetVersion: '1.3.0',
    resolvedInVersion: '1.3.0',
    reportedDate: '2026-05-07',
    resolvedDate: '2026-05-07',
  },
  {
    id: 'IMP-003',
    title: 'Sprung Leistungsverzeichnis ↔ Baumdetail',
    description: 'In der LV-Detail-Tabelle ist die Baumnummer jetzt klickbar (unterstrichen, primary-farben). Klick → Baumblatt mit kontextuellem Zurück-Button "Zurück zum LV \\"…\\"". Der Zurück-Button öffnet das LV-Detail wieder im richtigen Tab. Backend liefert tree_id über JOIN auf tree_measures.',
    area: 'Leistungsverzeichnis',
    category: 'Verbesserung',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.3.0',
    resolvedInVersion: '1.3.0',
    reportedDate: '2026-05-07',
    resolvedDate: '2026-05-07',
  },
  {
    id: 'IMP-004',
    title: 'Baumtabelle: Filter "Abgeschlossene Maßnahmen"',
    description: 'Filter-Panel komplett neu strukturiert: drei Single-Select-Chip-Gruppen für Maßnahmen (Alle / Offen / Erledigt / Keine), Schäden (Alle / Offen / Behoben / Keine) und Kontrollen (Alle / Vorhanden / Keine). Default "Alle" lässt den Filter inaktiv. Backend erweitert um massnahmen_status, schaeden_status, kontrollen_status Query-Parameter mit toleranter Status-Logik (erkennt "erledigt" und "abgeschlossen" als done, "erledigt" und "behoben" als behoben).',
    area: 'Baumkataster',
    category: 'Verbesserung',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.3.0',
    resolvedInVersion: '1.3.0',
    reportedDate: '2026-05-07',
    resolvedDate: '2026-05-07',
  },

  // ── Priorität 4 – Neue Features ──────────────────────────────────────────
  {
    id: 'FEAT-001',
    title: 'Luftbild-Layer auf der Karte',
    description: 'Layer-Switcher oben rechts auf der Karte (ToggleButtonGroup mit "Karte" und "Luftbild"). Standard-Layer ist OpenStreetMap, Luftbild kommt von Esri World Imagery (gratis, kein API-Key). Hilft beim Verifizieren von Baum-Positionen.',
    area: 'Karte',
    category: 'Feature',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.3.0',
    resolvedInVersion: '1.3.0',
    reportedDate: '2026-05-07',
    resolvedDate: '2026-05-07',
  },
  {
    id: 'FEAT-002',
    title: 'GPS-Ortung auf der Karte',
    description: 'GPS-Button links unten auf der Karte (Material-Icon MyLocation). Klick ermittelt die aktuelle Position über navigator.geolocation, zoomt zur Position und zeigt einen blauen Marker mit Genauigkeitsradius. Verwendet enableHighAccuracy. Klare Fehlermeldungen bei verweigertem Zugriff, fehlender Verfügbarkeit oder Timeout.',
    area: 'Karte',
    category: 'Feature',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.3.0',
    resolvedInVersion: '1.3.0',
    reportedDate: '2026-05-07',
    resolvedDate: '2026-05-07',
  },

  // ── Nachträgliche Verbesserungen v1.3.0 ──────────────────────────────────
  {
    id: 'IMP-005',
    title: 'Baumerfassung: Mass-Workflow & Felder erweitern',
    description: 'Mehrere Verbesserungen für die Baumerfassung auf der Karte: (1) Nach Bestätigung bleibt man auf der Karte (kein Sprung zum Baumdetail) – ermöglicht schnelles Erfassen mehrerer Bäume hintereinander, (2) Snackbar-Bestätigung statt Navigation, (3) Eingabefelder erweitert um Höhe, Stammdurchmesser, Kronendurchmesser, Kronenansatz, (4) "Werte merken"-Sektion: Nutzer kann pro Feld auswählen welche Werte für die nächste Erfassung übernommen werden sollen, (5) Orange Hinweis-Box im Dialog zeigt aktive Memo-Felder.',
    area: 'Karte',
    category: 'Verbesserung',
    priority: 'mittel',
    status: 'erledigt',
    targetVersion: '1.3.0',
    resolvedInVersion: '1.3.0',
    reportedDate: '2026-05-07',
    resolvedDate: '2026-05-07',
  },
  {
    id: 'BUG-008',
    title: '"+"-Icon im Hinzufügen-Button nicht zentriert',
    description: 'Auf Mobile bricht der Button-Text "Neuen Baum hinzufügen" auf zwei Zeilen, dadurch wirkte das Plus-Icon vertikal versetzt. Fix: alignSelf auf das startIcon gesetzt.',
    area: 'Karte',
    category: 'Bug',
    priority: 'niedrig',
    status: 'erledigt',
    targetVersion: '1.3.0',
    resolvedInVersion: '1.3.0',
    reportedDate: '2026-05-07',
    resolvedDate: '2026-05-07',
  },
  {
    id: 'BUG-009',
    title: 'Cursor im GPS-Genauigkeitskreis nicht crosshair',
    description: 'Beim Erfassen mit aktiver GPS-Position zeigte der Cursor im blauen Genauigkeitskreis einen Hand-Pointer statt Fadenkreuz. Ursache: Leaflet Path/Circle-Layer fingen Pointer-Events ab. Fix: interactive=false auf den Genauigkeitskreis.',
    area: 'Karte',
    category: 'Bug',
    priority: 'niedrig',
    status: 'erledigt',
    targetVersion: '1.3.0',
    resolvedInVersion: '1.3.0',
    reportedDate: '2026-05-07',
    resolvedDate: '2026-05-07',
  },

  // ── Tech-Debt / Aufräumen ────────────────────────────────────────────────
  {
    id: 'TECH-001',
    title: 'Status-Werte für Maßnahmen vereinheitlichen',
    description: 'DB-Migration: UPDATE tree_measures SET status = \'erledigt\' WHERE status = \'abgeschlossen\'; UPDATE tree_measures SET status = \'offen\' WHERE status = \'geplant\'. CHECK-Constraint hinzugefügt (nur \'offen\', \'erledigt\', \'abgebrochen\' erlaubt). Code-Toleranz in Auswertung.tsx, TreeDetail.tsx und trees.routes.ts entfernt – Status-Vergleiche sind jetzt strikt. Default der Spalte ist \'offen\'.',
    area: 'Datenbank',
    category: 'Verbesserung',
    priority: 'niedrig',
    status: 'erledigt',
    targetVersion: '1.4.0',
    resolvedInVersion: '1.4.0',
    reportedDate: '2026-05-07',
    resolvedDate: '2026-05-12',
  },
];
